import React from 'react';
import { useTranslation } from 'react-i18next';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Typography, IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';

import { missionPlannerStore } from '../../stores/MissionStore';
import { TakeOffAction, GoToWaypointAction } from '../../types/Mission';
import DynamicFormFields, { FormFieldConfig } from './DynamicFormFields';
import TakeOffIcon from './icons/TakeOffIcon.svg?react';
import WaypointIcon from './icons/WaypointIcon.svg?react';
import { MapItem, MapItemType } from './MapItem';

interface MissionItemParametersProps {
  items: MapItem[];
  selectedItem?: MapItem;
  hoveredItem?: MapItem;
  onItemSelect?: (item: MapItem) => void;
  onItemHover?: (item: MapItem | undefined) => void;
  onItemDelete?: (item: MapItem) => void;
  onClose?: () => void;
  isDeleteDisabled?: boolean;
}

const MissionItemParameters: React.FC<MissionItemParametersProps> = observer(
  ({ items, selectedItem, hoveredItem, onItemSelect, onItemHover, onItemDelete, onClose, isDeleteDisabled }) => {
    const { t } = useTranslation();

    const getIconBackgroundColor = (item: MapItem) => {
      if (selectedItem?.type === item.type && selectedItem?.index === item.index) return '#888';
      if (hoveredItem?.type === item.type && hoveredItem?.index === item.index) return '#333';
      return '#000';
    };

    const getFormFields = (item: MapItem): { fields: Record<string, FormFieldConfig>; order: string[] } => {
      let missionAction = null;

      switch (item.type) {
        case MapItemType.Takeoff:
          missionAction = missionPlannerStore.missionActions[0] as TakeOffAction;
          break;
        case MapItemType.Waypoint:
          missionAction = missionPlannerStore.missionActions[item.index! - 1] as GoToWaypointAction;
          break;
        default:
          break;
      }

      const baseFields: Record<string, FormFieldConfig> = {
        latitude: {
          key: 'latitude',
          label: t('mission_planning.latitude'),
          value: Math.round(missionAction!.location[1] * 1000000) / 1000000,
          type: 'number',
          readOnly: false,
          step: 0.000001
        },
        longitude: {
          key: 'longitude',
          label: t('mission_planning.longitude'),
          value: Math.round(missionAction!.location[0] * 1000000) / 1000000,
          type: 'number',
          readOnly: false,
          step: 0.000001
        }
      };

      if (item.type === MapItemType.Takeoff) {
        const takeoffAction = missionAction as TakeOffAction;
        return {
          fields: {
            verticalSpeed: {
              key: 'verticalSpeed',
              label: t('mission_planning.speed'),
              value: takeoffAction.verticalSpeed.toString(),
              unit: t('mission_planning.mps'),
              type: 'number',
              readOnly: false
            },
            altitude: {
              key: 'altitude',
              label: t('mission_planning.altitude'),
              value: takeoffAction.altitude.toString(),
              unit: t('mission_planning.feet_short'),
              type: 'number',
              readOnly: false
            },
            ...baseFields
          },
          order: ['verticalSpeed', 'altitude', 'latitude', 'longitude']
        };
      }
      if (item.type === MapItemType.Waypoint) {
        const waypointAction = missionAction as GoToWaypointAction;
        return {
          fields: {
            speed: {
              key: 'speed',
              label: t('mission_planning.speed'),
              value: waypointAction.speed.toString(),
              unit: t('mission_planning.mph'),
              type: 'number',
              readOnly: false
            },
            altitude: {
              key: 'altitude',
              label: t('mission_planning.altitude'),
              value: waypointAction.altitude.toString(),
              unit: t('mission_planning.feet_short'),
              type: 'number',
              readOnly: false
            },
            ...baseFields
          },
          order: ['speed', 'altitude', 'latitude', 'longitude']
        };
      }

      return {
        fields: baseFields,
        order: ['latitude', 'longitude']
      };
    };

    const handleFieldChange = (key: string, value: string | number) => {
      if (!selectedItem) return;

      const numericValue = typeof value === 'string' ? parseFloat(value) : value;

      if (selectedItem.type === MapItemType.Takeoff) {
        const storeIndex = 0;
        const currentAction = missionPlannerStore.missionActions[storeIndex] as TakeOffAction;
        const updates: Partial<TakeOffAction> = {};

        switch (key) {
          case 'latitude':
            updates.location = [currentAction.location[0], numericValue];
            break;
          case 'longitude':
            updates.location = [numericValue, currentAction.location[1]];
            break;
          case 'altitude':
            updates.altitude = numericValue;
            break;
          case 'verticalSpeed':
            updates.verticalSpeed = numericValue;
            break;
          default:
            break;
        }

        missionPlannerStore.updateMissionAction(storeIndex, updates);
      } else if (selectedItem.type === MapItemType.Waypoint) {
        const storeIndex = selectedItem.index! - 1;
        const currentAction = missionPlannerStore.missionActions[storeIndex] as GoToWaypointAction;
        const updates: Partial<GoToWaypointAction> = {};

        switch (key) {
          case 'latitude':
            updates.location = [currentAction.location[0], numericValue];
            break;
          case 'longitude':
            updates.location = [numericValue, currentAction.location[1]];
            break;
          case 'altitude':
            updates.altitude = numericValue;
            break;
          case 'speed':
            updates.speed = numericValue;
            break;
          default:
            break;
        }

        missionPlannerStore.updateMissionAction(storeIndex, updates);
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          borderRadius: 'sm',
          overflow: 'hidden',
          height: '400px'
        }}
      >
        <Box
          sx={{
            width: '25px',
            height: '400px',
            backgroundColor: '#FBBC39', // bumblebee yellow
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 1,
            gap: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none'
          }}
        >
          {items.map((item, index) => (
            <Box
              key={`sequence-${item.type}-${item.index}`}
              sx={{
                width: '20px',
                height: '45px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                level='body-sm'
                sx={{
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                {index + 1}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            width: '45px',
            height: '400px',
            backgroundColor: '#FBBC39', // bumblebee yellow
            opacity: 0.7,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 1,
            gap: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none'
          }}
        >
          {items.map((item, index) => (
            <Box
              key={`${item.type}-${item.index || index}`}
              onClick={() => onItemSelect?.(item)}
              onMouseEnter={() => onItemHover?.(item)}
              onMouseLeave={() => onItemHover?.(undefined)}
              sx={{
                width: '45px',
                height: '45px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: getIconBackgroundColor(item),
                cursor: 'pointer'
              }}
            >
              {
                {
                  [MapItemType.Takeoff]: (
                    <TakeOffIcon
                      width={36}
                      height={36}
                      fill={selectedItem === item ? '#000' : '#fff'}
                      stroke={selectedItem === item ? '#000' : '#fff'}
                      strokeWidth={2}
                    />
                  ),
                  [MapItemType.Waypoint]: (
                    <WaypointIcon
                      width={36}
                      height={36}
                      fill={selectedItem === item ? '#000' : '#fff'}
                      stroke={selectedItem === item ? '#000' : '#fff'}
                      strokeWidth={2}
                    />
                  )
                }[item.type]
              }
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            width: '360px',
            height: '400px',
            backgroundColor: 'neutral.900',
            borderStyle: 'solid',
            borderColor: 'neutral.700',
            borderWidth: '1px 1px 1px 0',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {selectedItem && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 2,
                  backgroundColor: 'neutral.800',
                  borderBottom: '1px solid',
                  borderColor: 'neutral.700'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size='sm'
                    variant='plain'
                    onClick={onClose}
                    sx={{ color: 'neutral.400', minHeight: 'auto', minWidth: 'auto', padding: 0 }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                  <Typography level='title-md' sx={{ color: 'neutral.100', textTransform: 'capitalize' }}>
                    {selectedItem.type === MapItemType.Takeoff
                      ? t('mission_planning.takeoff')
                      : t('mission_planning.waypoint')}
                  </Typography>
                </Box>
                <IconButton
                  size='sm'
                  variant='plain'
                  disabled={isDeleteDisabled}
                  onClick={() => selectedItem && !isDeleteDisabled && onItemDelete?.(selectedItem)}
                  sx={{
                    color: isDeleteDisabled ? 'neutral.600' : 'neutral.400',
                    cursor: isDeleteDisabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Box sx={{ padding: 2, flex: 1 }}>
                {(() => {
                  const { fields, order } = getFormFields(selectedItem);
                  return <DynamicFormFields fields={fields} fieldOrder={order} onChange={handleFieldChange} />;
                })()}
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }
);

export default MissionItemParameters;
